import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Radio,
  Users,
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  Calendar,
  MessageSquare,
  AlertTriangle,
  Smartphone,
  Zap,
  Variable,
  RefreshCw,
} from 'lucide-react';
import { apiFetch } from '../lib/api';

// ============================================================================
// Types
// ============================================================================

interface BroadcastTask {
  id: number;
  name: string;
  description?: string;
  targetType: string;
  templateContent: string;
  status: 'pending' | 'sending' | 'completed' | 'cancelled';
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  scheduledAt: string;
  startedAt?: string;
  completedAt?: string;
  hourlyCap: number;
  minIntervalSeconds?: number;
  maxIntervalSeconds?: number;
  createdAt: string;
}

interface BroadcastLog {
  id: number;
  donorId?: number;
  donorName?: string;
  donorPhone: string;
  messageContent: string;
  sentAt?: string;
  status: string;
  errorMessage?: string;
}

// ============================================================================
// Constants
// ============================================================================

const statusConfig: Record<string, { label: string; className: string; icon: React.ElementType }> = {
  pending: { label: '待發送', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  sending: { label: '發送中', className: 'bg-blue-100 text-blue-700 border-blue-200', icon: Loader2 },
  completed: { label: '已完成', className: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  cancelled: { label: '已取消', className: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle },
};

const targetTypeLabels: Record<string, string> = {
  all: '全部捐贈者',
  monthly: '月捐用戶',
  'one-time': '一次性捐贈者',
  tags: '指定標籤',
};

const logStatusConfig: Record<string, { label: string; className: string; icon: React.ElementType }> = {
  sent: { label: '成功', className: 'text-green-600', icon: CheckCircle },
  success: { label: '成功', className: 'text-green-600', icon: CheckCircle },
  failed: { label: '失敗', className: 'text-red-600', icon: XCircle },
  pending: { label: '待發送', className: 'text-amber-600', icon: Clock },
};

// ============================================================================
// Helpers
// ============================================================================

function highlightVariables(text: string): React.ReactNode {
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  return parts.map((part, i) => {
    if (part.match(/^\{\{[^}]+\}\}$/)) {
      return (
        <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 text-xs font-mono mx-0.5">
          <Variable className="w-3 h-3 mr-0.5" />
          {part}
        </span>
      );
    }
    return part;
  });
}

// ============================================================================
// Component
// ============================================================================

export default function AdminBroadcastDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState<BroadcastTask | null>(null);
  const [logs, setLogs] = useState<BroadcastLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [logFilter, setLogFilter] = useState<'all' | 'sent' | 'failed' | 'pending'>('all');

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setRefreshing(true);
    const res = await apiFetch<{ task: BroadcastTask; logs: BroadcastLog[] }>(`/api/admin/broadcast-tasks/${id}`);
    if (res.success && res.data) {
      setTask(res.data.task);
      setLogs(res.data.logs);
    }
    setRefreshing(false);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Auto refresh for sending tasks
  useEffect(() => {
    if (task?.status === 'sending') {
      const interval = setInterval(fetchDetail, 5000);
      return () => clearInterval(interval);
    }
  }, [task?.status, fetchDetail]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">找不到廣播任務</p>
        <button onClick={() => navigate('/admin/broadcast-tasks')} className="btn-secondary">
          <ArrowLeft className="w-4 h-4" />
          返回廣播列表
        </button>
      </div>
    );
  }

  const status = statusConfig[task.status];
  const StatusIcon = status.icon;
  const progress = task.totalRecipients > 0 ? Math.round((task.sentCount / task.totalRecipients) * 100) : 0;
  const successRate = task.sentCount > 0 ? Math.round(((task.sentCount - task.failedCount) / task.sentCount) * 100) : 0;

  const filteredLogs = logs.filter((log) => {
    if (logFilter === 'all') return true;
    if (logFilter === 'sent') return log.status === 'sent' || log.status === 'success';
    if (logFilter === 'failed') return log.status === 'failed';
    if (logFilter === 'pending') return log.status === 'pending';
    return true;
  });

  const pendingCount = logs.filter((l) => l.status === 'pending').length;
  const sentCount = logs.filter((l) => l.status === 'sent' || l.status === 'success').length;
  const failedCount = logs.filter((l) => l.status === 'failed').length;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate('/admin/broadcast-tasks')}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 truncate">廣播詳情</h1>
          <p className="text-sm text-gray-500">{task.name}</p>
        </div>
        <button
          onClick={fetchDetail}
          disabled={refreshing}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          刷新
        </button>
      </div>

      {/* Status Banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center gap-3 px-5 py-4 rounded-2xl border ${status.className}`}
      >
        <StatusIcon className={`w-6 h-6 ${task.status === 'sending' ? 'animate-spin' : ''}`} />
        <div>
          <div className="font-bold text-lg">{status.label}</div>
          <div className="text-sm opacity-80">
            {task.status === 'pending' && `預定發送時間：${new Date(task.scheduledAt).toLocaleString('zh-HK')}`}
            {task.status === 'sending' && '訊息正在發送中，請稍後查看進度'}
            {task.status === 'completed' && `完成時間：${task.completedAt ? new Date(task.completedAt).toLocaleString('zh-HK') : '—'}`}
            {task.status === 'cancelled' && '此任務已被取消'}
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="card p-5"
        >
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <Users className="w-4 h-4" />
            總收件人
          </div>
          <div className="text-2xl font-extrabold text-gray-900">{task.totalRecipients.toLocaleString()}</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-5"
        >
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            已成功
          </div>
          <div className="text-2xl font-extrabold text-green-600">{task.sentCount.toLocaleString()}</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card p-5"
        >
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <XCircle className="w-4 h-4 text-red-500" />
            失敗
          </div>
          <div className="text-2xl font-extrabold text-red-600">{task.failedCount.toLocaleString()}</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-5"
        >
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <Radio className="w-4 h-4" />
            完成度
          </div>
          <div className="text-2xl font-extrabold text-brand-600">{progress}%</div>
        </motion.div>
      </div>

      {/* Progress Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">發送進度</span>
          <span className="text-sm text-gray-500">
            {task.sentCount.toLocaleString()} / {task.totalRecipients.toLocaleString()}
            {task.failedCount > 0 && (
              <span className="text-red-500 ml-2">（失敗 {task.failedCount}）</span>
            )}
          </span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-brand-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>
        {task.sentCount > 0 && (
          <div className="mt-2 text-xs text-gray-400">
            成功率：{successRate}%
          </div>
        )}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Task Info & Template */}
        <div className="lg:col-span-1 space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="card p-6 space-y-4"
          >
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-600" />
              任務資訊
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">目標群組</span>
                <span className="font-medium text-gray-900">{targetTypeLabels[task.targetType] || task.targetType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">每小時上限</span>
                <span className="font-medium text-gray-900">{task.hourlyCap} 則</span>
              </div>
              {task.minIntervalSeconds && task.maxIntervalSeconds && (
                <div className="flex justify-between">
                  <span className="text-gray-500">發送間隔</span>
                  <span className="font-medium text-gray-900">{task.minIntervalSeconds} - {task.maxIntervalSeconds} 秒</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">建立時間</span>
                <span className="font-medium text-gray-900">{new Date(task.createdAt).toLocaleString('zh-HK')}</span>
              </div>
              {task.startedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">開始時間</span>
                  <span className="font-medium text-gray-900">{new Date(task.startedAt).toLocaleString('zh-HK')}</span>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35 }}
            className="card p-6 space-y-4"
          >
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-brand-600" />
              訊息模板
            </h2>
            <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {highlightVariables(task.templateContent)}
            </div>
          </motion.div>

          {/* Mini log stats */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="card p-6 space-y-3"
          >
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-brand-600" />
              發送統計
            </h2>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-amber-600">
                  <Clock className="w-4 h-4" />
                  待發送
                </span>
                <span className="font-medium">{pendingCount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  成功
                </span>
                <span className="font-medium">{sentCount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-red-600">
                  <XCircle className="w-4 h-4" />
                  失敗
                </span>
                <span className="font-medium">{failedCount}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right: Logs */}
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.35 }}
          className="lg:col-span-2"
        >
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-brand-600" />
                發送記錄
              </h2>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                {(['all', 'sent', 'failed', 'pending'] as const).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setLogFilter(filter)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                      logFilter === filter
                        ? 'bg-white text-brand-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {filter === 'all' && `全部 (${logs.length})`}
                    {filter === 'sent' && `成功 (${sentCount})`}
                    {filter === 'failed' && `失敗 (${failedCount})`}
                    {filter === 'pending' && `待發 (${pendingCount})`}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              {filteredLogs.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="text-gray-500">
                      <th className="px-4 py-3 text-left font-medium whitespace-nowrap">收件人</th>
                      <th className="px-4 py-3 text-left font-medium whitespace-nowrap">訊息內容</th>
                      <th className="px-4 py-3 text-left font-medium whitespace-nowrap">狀態</th>
                      <th className="px-4 py-3 text-left font-medium whitespace-nowrap">發送時間</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredLogs.map((log) => {
                      const logStatus = logStatusConfig[log.status] || logStatusConfig.pending;
                      const LogIcon = logStatus.icon;
                      return (
                        <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{log.donorName || '—'}</div>
                            <div className="text-xs text-gray-400">{log.donorPhone}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700 max-w-xs">
                            <div className="truncate" title={log.messageContent}>
                              {log.messageContent}
                            </div>
                            {log.errorMessage && (
                              <div className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{log.errorMessage}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 ${logStatus.className} text-xs font-medium`}>
                              <LogIcon className="w-4 h-4" />
                              {logStatus.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                            {log.sentAt ? new Date(log.sentAt).toLocaleString('zh-HK') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="px-4 py-12 text-center text-gray-400 text-sm">
                  暫無發送記錄
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
